/**
 * about-copy.ts — Plan 07-04 Task 1. Authored copy for the /about route.
 *
 * The page renders six sections in this exact order:
 *
 *   1. The Project — project description
 *   2. Why ~1925 — anchor-era rationale
 *   3. London Charter Commitment — paradata confidence-tier contract
 *   4. Audience — public-civic voice framing
 *   5. Historical Context — 1923 Klan rally ABOUT-04 acknowledgment (one
 *      paragraph, same visual weight as any other paragraph, Bhamwiki voice,
 *      no pull-quotes, no dedicated section heading, primary-source link)
 *   6. A Note on the Narration — AI-assisted narration disclosure (London
 *      Charter transparency extended to media beyond geometry)
 *
 * Voice discipline: declarative past tense, factual register, no commercial
 * language, no editorializing. Sourced verbatim from PROJECT.md, Phase 1
 * 01-HISTORICAL-RESEARCH.md Sec. 7, and the AESTHETIC-BRIEF rationale.
 *
 * Pitfall 6 (07-RESEARCH.md): the Klan paragraph is a single paragraph inside
 * the Historical-Context section alongside other factual context. The About
 * page renderer applies the SAME paragraph style to every <p> in the section;
 * the Klan paragraph is not styled differently.
 */

export type AboutSection = {
  id: string;
  heading: string;
  paragraphs: string[];
  /** Optional source-citation link (only present on the Historical-Context section). */
  klanParagraphSourceLink?: string;
  klanParagraphSourceLabel?: string;
};

export type AboutCopy = {
  pageTitle: string;
  sections: AboutSection[];
};

export const aboutCopy: AboutCopy = Object.freeze({
  pageTitle: 'About Edgewood Lake',
  sections: [
    {
      id: 'project',
      heading: 'The Project',
      paragraphs: [
        'Edgewood Lake is a browser-delivered 3D reconstruction of a vanished 117-acre commercial lake-and-pavilion resort that operated in Shades Valley, Alabama, from approximately 1915 to 1946. The resort was the centerpiece of Edgewood Highlands, a streetcar suburb platted around 1909 by Stephen Smith and Troupe Brazelton, and was reached from Birmingham by the 5.4-mile Birmingham & Edgewood Electric Railway through a deep cut at Lone Pine Gap.',
        'The lake was drained in 1946 and the bed conveyed to Howard College — now Samford University. The streets, a single historical marker, and the name "Lakeshore Drive" are what survive today. This reconstruction exists so that a stranger can open the link, ride the streetcar to Edgewood, walk the ~1925 lakeshore, and come away understanding what the vanished resort actually felt like.',
      ],
    },
    {
      id: 'anchor-era',
      heading: 'Why ~1925',
      paragraphs: [
        'The ~1925 anchor captures the peak of the resort era. Raymond R. Rochelle took over the grounds from the Birmingham Motor and Country Club in 1923 and operated Edgewood Park as a paid-admission public resort through the late 1930s. In the mid-1920s the lake was full and the clubhouse was standing; the No. 39 Edgewood streetcar was still delivering weekend crowds; the Dreamland dance hall had opened on the north shore. The year is the weighted center of the 1915 to 1940 operating window that the primary sources support.',
      ],
    },
    {
      id: 'london-charter',
      heading: 'London Charter Commitment',
      paragraphs: [
        'This project follows the London Charter for the Computer-Based Visualisation of Cultural Heritage and the Seville Principles for Virtual Archaeology. Every 3D structure in the scene carries a confidence tier: documented (sourced from period photograph, plat, or measured drawing), inferred (sourced from period-typical precedent and documented locational context), stand-in (period-vernacular-appropriate but not source-documented), or background-context (factual record, not a render target).',
        'The Aesthetic Brief locks a stylized painterly period-matte look — not photoreal. A visitor should look at any still from the experience and say "that looks like a careful model," never "is that a photograph?" The stylization is a deliberate transparency signal: reconstruction is interpretation, and the rendering should always acknowledge that.',
      ],
    },
    {
      id: 'audience',
      heading: 'Audience',
      paragraphs: [
        'Edgewood Lake is a public-civic resource. It is intended for anyone who lives in, has lived in, or cares about Birmingham’s Shades Valley and the suburb that became Homewood. It is not a family memoir, a commercial product, or a gamified experience. Educators can use the paradata citations and the Sources page to anchor lesson plans; academics can reach the primary sources through the same surface.',
      ],
    },
    {
      id: 'historical-context',
      heading: 'Historical Context',
      paragraphs: [
        'The 1923–1926 peak window shapes how the site is narrated. Homewood was not yet incorporated; the merger of Edgewood, Rosedale, and Grove Park into the City of Homewood did not occur until October 29, 1926. The lake operated through the mid-1920s under Rochelle; the streetcar ran regular service; the Dreamland dance hall operated on the north shore.',
        // Pitfall 6 — same visual weight, Bhamwiki voice, one paragraph, factual.
        'On September 11, 1923, the Robert E. Lee Klan No. 1 held a mass initiation ceremony on the drained bed of Edgewood Lake during the lake’s first dam-failure period. The Birmingham News reported approximately 25,000 attendees. Birmingham attorney Hugo L. Black, later a United States Senator and Justice of the U.S. Supreme Court, was initiated that night. James Esdale, who had been appointed Grand Dragon of the Alabama Realm earlier in 1923, presided over the Alabama organization at the time. The event is acknowledged here as a matter of primary-source record; it is not depicted in any scene, guided-tour narration, era toggle, or vista marker inside the 3D experience. See the Sources page for the primary-source citation.',
      ],
      klanParagraphSourceLink: '/sources#about04-klan-rally-edgewood-1923-09-11',
      klanParagraphSourceLabel: 'Primary-source citation →',
    },
    {
      id: 'narration-disclosure',
      heading: 'A Note on the Narration',
      paragraphs: [
        'The guided-tour narration on this site is AI-assisted — generated using ElevenLabs Multilingual v2 and disclosed in the captions track. A v1.1 release will replace the AI narration with a professional human voice actor. The London Charter transparency principle extends to all media on this site, not only the 3D geometry; see the Credits page for the full disclosure.',
      ],
    },
  ],
});
